import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Box, Text, Checkbox, Button, ButtonGroup } from '@chakra-ui/react';

import { Modal, AutocompleteInput, Select, Textarea, IOption } from 'shared-components';
import { IMessage } from 'types';

import { ApplicationState } from '../../../../store';
import { getAgents, getAgentsSuccess } from '../../../../store/data/actions';
import { saveMessage } from '../../../../store/student/actions';
import { updateAssignedAgents } from '../../../../store/student/core/actions';
import { Q } from '../../../../store/student/types';
import { IAgent, IAgentFromAPI } from '../../../../types';
import { API_CALLS } from '../../../../utilities/constants';
import { getDisplayName, slug } from '../../../../utilities/formatting';

import { AgentRow } from './AgentRow';
import { useTranslation } from "react-i18next";

export interface IProps {
  onClose: () => void;
}

enum Screen {
  View,
  Add,
  Remove,
}

const AgentModal = (props: IProps) => {
    const { t } = useTranslation('dialogs');
  // dispatch actions
  const dispatch = useDispatch();
  const getSuggestions = (searchQuery?: string) => dispatch(getAgents(searchQuery));
  const clearSuggestions = () => dispatch(getAgentsSuccess([]));
  const onSubmitMessage = (message: IMessage) => dispatch(saveMessage(message));
  const onUpdateAgents = (agents: IAgent[], lockAgent: boolean) => dispatch(updateAssignedAgents(agents, lockAgent));
  // state props from store
  const { auth, config, data, student } = useSelector(
    ({ auth, config, data, student }: ApplicationState) => ({ auth, config, data, student }),
    shallowEqual,
  );
  const { assignedAgents } = student.offer;
  const lockAgent = student.core.lockAgent ?? false;
  const visaSupport = student.offer.visaSupport ?? false;
  const userName = auth.student_number;
  const displayName = getDisplayName(auth, config);

  const { onClose } = props;

  const [screen, setScreen] = useState(Screen.View);
  const [agents, setAgents] = useState(() => assignedAgents || []);
  const [isAgentLocked, setLockAgentValue] = useState<boolean>(lockAgent);

  const [newAgent, setNewAgent] = useState<IAgent>(() => ({
    id: '',
    type: 'master',
    displayName: '',
    note: '',
    agentName: '',
    contactEmail: '',
  }));
  const [agentName, setAgentName] = useState('');
  const [removeIndex, setRemoveIndex] = useState(-1);
  const [removalReason, setRemovalReason] = useState('');
  const [agentSuggestions, setAgentSuggestions] = useState<IAgentFromAPI[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true); // default to loading first

  useEffect(() => {
    const isLoading = data.apiCallsInProgress.find(x => x.callName === API_CALLS.GET_AGENTS) !== undefined;
    if (isLoadingSuggestions !== isLoading) {
      setIsLoadingSuggestions(isLoading);
    }
  }, [data, isLoadingSuggestions]);

  useEffect(() => {
    if (data.agentSuggestions && JSON.stringify(agentSuggestions) !== JSON.stringify(data.agentSuggestions)) {
      setAgentSuggestions(data.agentSuggestions);
    }
  }, [data, agentSuggestions]);

  const typeOptions: IOption[] = [
    {
      label: 'Master Agent',
      value: 'master',
    },
    {
      label: 'Sub Agent',
      value: 'sub',
    },
    {
      label: 'Other',
      value: 'other',
    },
  ];

  const getAgentTypeLabel = (type: IAgent['type']): string => {
    switch (type) {
      case 'master':
        return '(master)';
      case 'sub':
        return '(sub)';
      case 'other':
        return '';
      case 'student':
        return '(student selected)';
    }
  };

  const getTitle = (screen: Screen): string => {
    switch (screen) {
      case Screen.Add:
        return 'Add Agent';
      case Screen.Remove:
        return 'Remove Agent';
      case Screen.View:
      default:
        return 'Agent Access';
    }
  };

  const visaSupportAgent: IAgent = {
    type: 'student',
    id: 'EnrolyVIS',
    agentName: 'EnrolyVIS',
    displayName: 'Visa Support Opt In',
  };

  const handleTypeChange = (value: string) => {
    setNewAgent((agent: any) => {
      return {
        ...agent,
        type: value,
      };
    });
  };

  const handleSuggestionChange = (evt: any) => {
    const value = evt.currentTarget.value || '';

    setAgentName(value);

    if (agentSuggestions.find(x => slug(x.displayName) === slug(value))) {
      handleAgentNameChange(value, false);
    } else {
      getSuggestions(value);
    }
  };

  const onAgentNameBlur = (event: React.FormEvent<HTMLInputElement>) => {
    if (event && event.currentTarget) {
      const { value } = event.currentTarget;
      handleAgentNameChange(value, true);
    }
  };

  const handleAgentNameChange = (value: string, onBlur: boolean) => {
    const selectedAgent = agentSuggestions.find(x => slug(x.displayName) === slug(value));

    if (selectedAgent) {
      setNewAgent(agent => {
        return {
          ...agent,
          id: selectedAgent.id,
          displayName: selectedAgent.displayName,
          agentName: selectedAgent.agentName,
          contactEmail: selectedAgent.contactEmail,
        };
      });
    } else if (onBlur) {
      setAgentName('');
      setNewAgent(agent => ({
        ...agent,
        id: '',
        displayName: '',
        agentName: '',
        contactEmail: '',
      }));
      clearSuggestions();
    }
  };

  const handleNoteChange = (value: string) => {
    setNewAgent((agent: any) => {
      return {
        ...agent,
        note: value,
      };
    });
  };

  const handleRemovalReasonChange = (value: string) => {
    setRemovalReason(value);
  };

  const addAgent = () => {
    const selectedAgent = agentSuggestions.find(x => slug(x.displayName) === slug(newAgent.displayName));

    if (selectedAgent && newAgent.type) {
      // removing special <Removed> entries if real agent was added
      const newAgents = agents.filter(agent => agent.displayName !== '<Removed>');
      newAgents.push(newAgent);

      if (newAgent.note) {
        const author = userName || 'Anonymous';
        const message: IMessage = {
          message: `Agent ${newAgent.displayName} Added: ${newAgent.note}`,
          createdAt: Date.now(),
          isSendToStudent: false,
          messageType: 'note',
          author,
          authorName: displayName || author,
          messageFrom: 'uni',
          messageTo: 'uni',
        };
        onSubmitMessage(message);
      }
      setAgents(newAgents);
      onUpdateAgents(newAgents, isAgentLocked);

      setNewAgent({
        id: '',
        type: 'master',
        displayName: '',
        note: '',
        agentName: '',
        contactEmail: '',
      });
      setScreen(Screen.View);
    }
  };

  const removeAgent = () => {
    if (removeIndex >= 0) {
      const newAgents = [...agents];
      const removedAgent = agents[removeIndex];
      newAgents.splice(removeIndex, 1);
      // adding special <Removed> entry if no agents left
      if (newAgents.length === 0) {
        newAgents.push({
          type: 'student',
          displayName: '<Removed>',
        });
      }

      const msgReason = removalReason ? `Removed: ${removalReason}` : 'Removed';
      const author = userName || 'Anonymous';
      const message: IMessage = {
        message: `Agent ${removedAgent.displayName} ${msgReason}`,
        createdAt: Date.now(),
        isSendToStudent: false,
        messageType: 'note',
        author,
        authorName: displayName || author,
        messageFrom: 'uni',
        messageTo: 'uni',
      };

      setAgents(newAgents);
      onUpdateAgents(newAgents, false);
      onSubmitMessage(message);

      setRemoveIndex(-1);
      setScreen(Screen.View);
    }
  };

  return (
    <Modal
      id="agent-view"
      show={true}
      title={getTitle(screen)}
      onClose={onClose}
      footer={
        <ButtonGroup size={'lg'} spacing={3}>
          {screen === Screen.View ? (
            <Button id="close-button" onClick={onClose} variant={'solid'}>
              Close
            </Button>
          ) : (
            <Button id="back-button" onClick={() => setScreen(Screen.View)}>
              Back
            </Button>
          )}

          {screen === Screen.Add && (
            <Button id="assign-agent-button" onClick={addAgent} variant={'solid'}>
              Assign Agent
            </Button>
          )}

          {screen === Screen.Remove && removeIndex >= 0 && (
            <Button id="remove-agent-button" onClick={removeAgent} variant={'solid'}>
              Remove Agent
            </Button>
          )}
        </ButtonGroup>
      }
    >
      {screen === Screen.View && (
        <>
          <>
            <Text fontSize={'lg'} fontWeight={'medium'} mb={2}>
              {t('dialogs:agents')}
                                      </Text>

            {visaSupport ? (
              <AgentRow name={visaSupportAgent.displayName} type={getAgentTypeLabel(visaSupportAgent.type)} />
            ) : null}

            {agents.map((agent, i) => {
              return (
                <AgentRow
                  key={i}
                  name={agent.displayName}
                  type={getAgentTypeLabel(agent.type)}
                  isAgentLocked={isAgentLocked}
                  isRemovable={agent.displayName !== '<Removed>'}
                  removeOnClick={() => {
                    setRemoveIndex(i);
                    setScreen(Screen.Remove);
                  }}
                />
              );
            })}

            <Button onClick={() => setScreen(Screen.Add)}>{t('dialogs:addAnotherAgent')}</Button>
          </>
        </>
      )}

      {screen === Screen.Add && (
        <>
          <Select
            label={t('dialogs:type')}
            options={typeOptions}
            value={newAgent.type}
            placeHolder={t('dialogs:select')}
            onChange={(evt: React.FormEvent<HTMLSelectElement>) => handleTypeChange(evt.currentTarget.value)}
            mb={6}
          />

          <AutocompleteInput
            key={Q.AGENT_NAME}
            id={Q.AGENT_NAME}
            label={t('dialogs:selectAgentName')}
            onChange={(evt: React.FormEvent<HTMLInputElement>) => handleSuggestionChange(evt)}
            value={agentName}
            suggestions={agentSuggestions.map(x => x.displayName)}
            suggestionsLoading={isLoadingSuggestions}
            onBlur={onAgentNameBlur}
            mb={6}
          />

          <Checkbox
            id={'agentAccess'}
            onChange={() => {
              setLockAgentValue(!isAgentLocked);
            }}
            isChecked={isAgentLocked}
            mb={6}
          >
            {t('dialogs:lockAgentPreventAgentChangeByUpl')}
                                </Checkbox>

          <Textarea
            value={newAgent.note}
            label={t('dialogs:addNote')}
            onChange={(evt: React.FormEvent<HTMLTextAreaElement>) => handleNoteChange(evt.currentTarget.value)}
          />
        </>
      )}

      {screen === Screen.Remove && removeIndex >= 0 && (
        <>
          <Box mb={6}>
            <Text fontSize={'lg'} fontWeight={'medium'} mb={2}>
              {t('dialogs:removing')}
                                      </Text>

            <AgentRow name={agents[removeIndex].displayName} type={getAgentTypeLabel(agents[removeIndex].type)} />
          </Box>

          <Textarea
            value={removalReason}
            label={t('dialogs:removeReason')}
            onChange={(evt: React.FormEvent<HTMLTextAreaElement>) => handleRemovalReasonChange(evt.currentTarget.value)}
            note="Note: Student selected agents will still appear in the CAS Checklist"
          />
        </>
      )}
    </Modal>
  );
};

export default AgentModal;
